import { useSenderAccountContext } from "context/SenderAccountContext";
import { DOCS_FORM_NAMES } from "core/consts";
import { getIsDtaActive } from "core/model/productfeatures/utils";
import {
  Auction,
  AuctionForm,
  DocsFormDataMapped,
  DocsFormName,
  DocsFormPath,
  GetOntologyType,
  Patient,
  SchemaVersion,
  ValueOf,
} from "core/types";
import type { Locale } from "date-fns";
import { PDFForm } from "pdf-lib";
import { ReactNode } from "react";
import { DefaultValues } from "react-hook-form";
import Translations from "translations/types";
import { z } from "zod";
import { SourceInformation } from "../useDocsForm";
import { getDRVGeneralFormConfig } from "./DRVGeneral";
import { getDRVMedicalFormConfig } from "./DRVMedical";
import { getDischargeLetterAccidentSurgeryFormConfig } from "./DischargeLetters/AccidentSurgery";
import { getDischargeLetterCardiologyFormConfig } from "./DischargeLetters/Cardiology";
import { getDischargeLetterENTFormConfig } from "./DischargeLetters/ENT";
import { getDischargeLetterNeurosurgeryFormConfig } from "./DischargeLetters/NeuroSurgery";
import { getGKVGeneralFormConfig } from "./GKVGeneral";
import { getGKVMedicalFormConfig } from "./GKVMedical";
import { getTransitionalCareFormConfig } from "./TransitionalCare";

export type DocsFormFormGetters<T extends DocsFormName> = Partial<
  Record<
    keyof DocsFormDataMapped<T>,
    (auction: Auction) => ValueOf<DocsFormDataMapped<T>> | null
  >
>;

export type FormFieldConfigProps<T extends DocsFormName> = {
  auction?: Auction;
  ariaLabelledById?: string;
  formName: T;
  schemaVersion?: SchemaVersion;
  skipRequiredValidation?: boolean;
  translations: Translations;
  sourceInformation: SourceInformation<T>;
};

export type PdfFillerProps<T extends DocsFormName> = {
  form: PDFForm;
  formData: DocsFormDataMapped<T>;
  locale: Locale;
  getOntology: GetOntologyType;
};

type PdfFiller<T extends DocsFormName> = ({
  form,
  formData,
  locale,
}: PdfFillerProps<T>) => void;

export type PdfCreatorProps<T extends DocsFormName> = {
  formData: DocsFormDataMapped<T>;
  translations: Translations;
  locale: Locale;
  createdBy?: string;
  getOntology: GetOntologyType;
};

export type PdfCreator<T extends DocsFormName> = (
  props: PdfCreatorProps<T>,
) => Promise<Uint8Array>;

export type PdfConfig<T extends DocsFormName> =
  | {
      downloadPdfFileName: (translations: Translations) => string;
      blankPdfAssetPath: DocsFormPath;
      pdfFiller: PdfFiller<T>;
      isXML?: false;
      pdfCreator?: never;
    }
  | {
      blankPdfAssetPath?: never;
      downloadPdfFileName: (translations: Translations) => string;
      pdfFiller?: never;
      isXML?: false;
      pdfCreator: PdfCreator<T>;
    }
  | {
      blankPdfAssetPath?: never;
      downloadPdfFileName: (translations: Translations) => string;
      pdfFiller?: never;
      isXML: true;
      pdfCreator?: never;
    };

export type FormFieldConfig<T extends DocsFormName> = {
  pdfConfig: PdfConfig<T>;
  schema: z.ZodType<DocsFormDataMapped<T>>;
  schemaVersion?: SchemaVersion;
  header: (props: { translations: Translations }) => string;
  config: { canPrint: boolean; canSendToHis: boolean };
  auctionToForm?: (
    getOntology: GetOntologyType,
    translations: Translations,
  ) => DocsFormFormGetters<T>;
  getInitialValue: (props: {
    auctionForms: AuctionForm[] | null | undefined;
    patient: Patient;
    value?: DocsFormDataMapped<T> | null | undefined;
  }) => DefaultValues<DocsFormDataMapped<T>>;
  sections: Array<{
    position: number;
    title?: (props: { translations: Translations }) => string | ReactNode;
    description?: (props: { translations: Translations }) => string;
    subTitle?: (props: { translations: Translations }) => string | ReactNode;
    fields: Partial<
      Record<
        keyof DocsFormDataMapped<T>,
        {
          subTitle?: {
            text: (props: { translations: Translations }) => string | ReactNode;
            className?: string;
            isRequired?: boolean;
          };
          description?: (props: { translations: Translations }) => string;
          input: (props: FormFieldConfigProps<T>) => ReactNode;
          position: number;
          hideSource?: boolean;
        }
      >
    >;
  }>;
};

export type FormFieldConfigFactory<T extends DocsFormName> = (args: {
  translations: Translations;
  skipRequiredValidation: boolean;
}) => FormFieldConfig<T>;

type FormFieldConfigRegistry = {
  [K in DocsFormName]: FormFieldConfigFactory<K>;
};

const FORM_FIELD_CONFIGS: FormFieldConfigRegistry = {
  [DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY]:
    getDischargeLetterCardiologyFormConfig,
  [DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT]: getDischargeLetterENTFormConfig,
  [DOCS_FORM_NAMES.DISCHARGE_LETTER_NEUROSURGERY]:
    getDischargeLetterNeurosurgeryFormConfig,
  [DOCS_FORM_NAMES.DISCHARGE_LETTER_TRAUMA_SURGERY]:
    getDischargeLetterAccidentSurgeryFormConfig,
  [DOCS_FORM_NAMES.DRV_GENERAL]: getDRVGeneralFormConfig,
  [DOCS_FORM_NAMES.DRV_MEDICAL]: getDRVMedicalFormConfig,
  [DOCS_FORM_NAMES.GKV_GENERAL]: getGKVGeneralFormConfig,
  [DOCS_FORM_NAMES.GKV_MEDICAL]: getGKVMedicalFormConfig,
  [DOCS_FORM_NAMES.TRANSITIONAL_CARE]: getTransitionalCareFormConfig,
};

export function useGetFormFieldConfig<T extends DocsFormName>(
  formName: T,
  translations: Translations,
) {
  const { careseeker } = useSenderAccountContext();
  const isDtaActivated = getIsDtaActive({ careseeker });

  const skipRequiredValidation =
    !isDtaActivated || formName !== DOCS_FORM_NAMES.GKV_GENERAL;

  const factory = FORM_FIELD_CONFIGS[formName];

  return {
    config: factory({ skipRequiredValidation, translations }),
    skipRequiredValidation,
  } as const;
}
